/**
 * ChatHeader 组件
 *
 * 聊天窗口头部组件，提供：
 * - 会话标题显示
 * - 新建会话按钮 (+)
 * - 会话历史列表按钮
 * - 刷新按钮
 */

import React, { useCallback } from 'react';
import { useChatStore } from '../../store/chatStore';
import { useAutoTrackStore } from '../../store/autoTrackStore';
import { ToggleSwitch } from '../ui/ToggleSwitch';

// 图标组件
const PlusIcon: React.FC<{ className?: string }> = ({ className = 'w-5 h-5' }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
  </svg>
);

const HistoryIcon: React.FC<{ className?: string }> = ({ className = 'w-5 h-5' }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);

const RefreshIcon: React.FC<{ className?: string }> = ({ className = 'w-5 h-5' }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
  </svg>
);

/**
 * ChatHeader组件属性接口
 */
interface ChatHeaderProps {
  /** 工作区ID */
  workspaceId: number;
  /** JWT认证令牌 */
  token: string;
  /** 会话列表是否显示 */
  showSessionList?: boolean;
  /** 切换会话列表显示状态 */
  onToggleSessionList?: () => void;
}

/**
 * ChatHeader组件
 */
export const ChatHeader: React.FC<ChatHeaderProps> = ({
  token,
  showSessionList = false,
  onToggleSessionList,
}) => {
  const {
    currentSession,
    setCurrentSession,
    loadSession,
    loading,
  } = useChatStore();

  const {
    enabled: autoTrackEnabled,
    toggleAutoTrack
  } = useAutoTrackStore();

  const handleNewSession = useCallback(async () => {
    if (loading) return;

    // 不再预创建空会话，只是清空当前会话
    // 用户发送消息时会自动创建新会话并生成标题
    // 这样可以避免创建标题为"新对话"的空会话
    setCurrentSession(null);
  }, [loading, setCurrentSession]);

  /**
   * 处理刷新会话
   */
  const handleRefresh = useCallback(async () => {
    if (loading || !currentSession) return;

    try {
      await loadSession(token, currentSession.id);
    } catch (error) {
      console.error('刷新会话失败:', error);
    }
  }, [token, currentSession, loading, loadSession]);

  /**
   * 获取会话标题
   */
  const sessionTitle = currentSession?.title || 'AI 智能创作';

  return (
    <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 bg-gray-50">
      <div className="flex items-center gap-3 min-w-0 flex-1">
        <div className="flex items-center gap-2 px-2 py-1 rounded-md bg-gray-100 shrink-0">
          <span className="text-xs text-gray-600 whitespace-nowrap">追踪</span>
          <ToggleSwitch
            checked={autoTrackEnabled}
            onChange={toggleAutoTrack}
            size="sm"
            ariaLabel="自动追踪开关"
          />
        </div>

        <div className="flex items-center gap-2 min-w-0">
          <span className="text-sm text-gray-900 font-medium truncate max-w-[150px] sm:max-w-[200px] md:max-w-[300px]">
            {sessionTitle}
          </span>
        </div>
      </div>

      <div className="flex items-center gap-1">
        <button
          onClick={handleRefresh}
          disabled={loading || !currentSession}
          className="p-2 text-gray-700 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          title="刷新"
          aria-label="刷新会话"
        >
          <RefreshIcon />
        </button>

        <div className="w-px h-5 bg-gray-300 mx-1" />

        <button
          onClick={onToggleSessionList}
          disabled={loading}
          className={`p-2 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
            showSessionList ? 'text-gray-900 bg-gray-100' : 'text-gray-700 hover:text-gray-900'
          }`}
          title="会话历史"
          aria-label="会话历史"
          aria-pressed={showSessionList}
        >
          <HistoryIcon />
        </button>

        <button
          onClick={handleNewSession}
          disabled={loading}
          className="p-2 text-gray-700 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          title="新建会话"
          aria-label="新建会话"
        >
          <PlusIcon />
        </button>
      </div>
    </div>
  );
};

export default ChatHeader;
